'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { getBrandAsset, type BrandSurface, type LogoVariant } from '@/lib/brand';
import { useTheme } from '@/components/theme-provider';
import { useMarca } from '@/components/brand/MarcaDeEmpresa';

const sizeClasses = {
  xs: 'h-7',
  sm: 'h-11',
  md: 'h-14',
  lg: 'h-16',
  xl: 'h-20',
  '2xl': 'h-28',
  '3xl': 'h-36',
  '4xl': 'h-44',
} as const;

const variantWidthClasses: Partial<Record<LogoVariant, string>> = {
  horizontal: 'min-w-[160px]',
  vertical: 'min-w-[96px]',
  wordmark: 'min-w-[140px]',
};

interface XtorageLogoProps {
  variant?: LogoVariant;
  size?: keyof typeof sizeClasses;
  surface?: BrandSurface;
  href?: string;
  className?: string;
  priority?: boolean;
}

export function XtorageLogo({
  variant = 'horizontal',
  size = 'md',
  surface,
  href,
  className,
  priority = false,
}: XtorageLogoProps) {
  const { theme } = useTheme();
  const resolvedSurface =
    surface ?? (theme === 'light' ? 'light' : 'dark');

  /**
   * EL LOGO DE LA EMPRESA, SI LO TIENE.
   *
   * `logo_url` llevaba en la base desde el principio y NO se usaba en ninguna
   * parte: se podia subir y la aplicacion seguia enseñando el de Xtorage.
   *
   * `useMarca()` devuelve null fuera de una empresa —login, portada, panel de
   * X-Cargo— y entonces se usa el de la casa, que es lo correcto: esas
   * pantallas no son de ningun cliente.
   */
  const marca = useMarca();
  const propio = marca?.logoUrl ?? null;

  const src = propio ?? getBrandAsset(variant, resolvedSurface);

  const image = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={marca?.siteTitle ?? 'Xtorage'}
      className={cn(
        sizeClasses[size],
        // El ancho minimo es para las tres variantes del logo de Xtorage, que
        // tienen proporciones conocidas. El de un cliente puede ser cuadrado o
        // muy alargado: forzarle un minimo lo deformaria o lo dejaria cortado.
        !propio && variantWidthClasses[variant],
        'w-auto max-w-none object-contain',
        className
      )}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
    />
  );

  if (href) {
    return (
      <Link href={href} className="inline-flex shrink-0 transition-opacity hover:opacity-90">
        {image}
      </Link>
    );
  }

  return image;
}
